import { useEffect, useState, useRef } from "react";
import { Gift, X, Zap, Clock } from "lucide-react";

const STORAGE_KEY = "trp_switcher_coupon";
const SHOWN_KEY = "trp_switcher_exit_intent_shown";
const BOOST_PCT = 10;
const BOOST_DURATION_S = 30;

/**
 * V187 — Exit-intent modal for /vs/<slug> pages.
 *
 * Fires once per session when the mouse moves above the viewport
 * (clientY < 0 with relatedTarget null = leaving the window). Offers
 * +10% extra discount on top of the existing switcher coupon. Has a
 * 30-second auto-dismiss countdown — the urgency is the entire point.
 *
 * On accept we mutate `localStorage.trp_switcher_coupon`:
 *   - code → `<original>-BOOST`
 *   - discount_pct → min(95, original + 10)
 *   - boost_accepted_at → ISO
 *
 * Backend recognises the `-BOOST` suffix in `validate_coupon()` so the
 * Stripe checkout flow automatically charges the new discounted amount.
 */
export default function SwitcherExitIntentModal({ slug }) {
  const [show, setShow] = useState(false);
  const [remaining, setRemaining] = useState(BOOST_DURATION_S);
  const [accepted, setAccepted] = useState(false);
  const armed = useRef(true);

  // Read original coupon for boost calculation
  const baseRef = useRef(null);

  function readBaseCoupon() {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch {}
    return null;
  }

  useEffect(() => {
    if (!slug) return;
    // Skip if already shown this session
    let shown = false;
    try { shown = window.sessionStorage.getItem(SHOWN_KEY) === "1"; } catch {}
    if (shown) return;

    const onLeave = (e) => {
      if (!armed.current) return;
      // mouseout at top edge — clientY < 0 means cursor moving toward close/tabs
      if (e.clientY > 0) return;
      // Re-read localStorage at fire time so we catch coupons issued AFTER mount
      const base = readBaseCoupon();
      if (!base?.code || base.code.endsWith("-BOOST")) return;
      baseRef.current = base;
      armed.current = false;
      try { window.sessionStorage.setItem(SHOWN_KEY, "1"); } catch {}
      setShow(true);
    };
    document.addEventListener("mouseout", onLeave);
    return () => document.removeEventListener("mouseout", onLeave);
  }, [slug]);

  useEffect(() => {
    if (!show || accepted) return;
    const id = setInterval(() => {
      setRemaining(r => {
        if (r <= 1) {
          clearInterval(id);
          setShow(false);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [show, accepted]);

  const accept = () => {
    const base = baseRef.current;
    if (!base) return;
    const boosted = {
      ...base,
      code: `${base.code}-BOOST`,
      discount_pct: Math.min(95, (base.discount_pct || 50) + BOOST_PCT),
      boost_accepted_at: new Date().toISOString(),
    };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(boosted)); } catch {}
    setAccepted(true);
    // Redirect to register so user actually checks out
    setTimeout(() => { window.location.href = "/register"; }, 1200);
  };

  if (!show) return null;

  const base = baseRef.current;
  const oldPct = base?.discount_pct || 50;
  const newPct = Math.min(95, oldPct + BOOST_PCT);
  const competitorName = (base?.from_slug || slug || "").charAt(0).toUpperCase() + (base?.from_slug || slug || "").slice(1);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      data-testid="switcher-exit-intent-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md bg-gradient-to-br from-violet-950 via-fuchsia-900 to-rose-900 text-white rounded-2xl shadow-2xl border border-white/15 p-6 lg:p-7">
        <button
          type="button"
          onClick={() => setShow(false)}
          className="absolute top-3 right-3 text-white/50 hover:text-white"
          aria-label="Dismiss"
          data-testid="switcher-exit-dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        {!accepted ? (
          <>
            <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-extrabold text-rose-300 bg-rose-500/15 border border-rose-400/30 rounded-full px-3 py-1 mb-4">
              <Zap className="w-3 h-3 animate-pulse" /> One-time offer
            </div>
            <h2 className="text-2xl lg:text-3xl font-black leading-tight mb-2">
              Wait! Switching from{" "}
              <span className="bg-gradient-to-r from-rose-300 to-amber-300 bg-clip-text text-transparent">
                {competitorName}
              </span>{" "}
              deserves more.
            </h2>
            <p className="text-white/80 text-sm mb-5 leading-relaxed">
              Add an extra <strong className="text-amber-300">+{BOOST_PCT}%</strong> on top of your current discount —
              just for trying TopRankPulse today. No questions asked.
            </p>

            <div className="bg-black/30 rounded-xl border border-white/15 p-4 mb-5 flex items-center justify-between gap-4">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-white/50 font-bold">Before</div>
                <div className="text-2xl font-black line-through opacity-60">{oldPct}%</div>
              </div>
              <div className="text-rose-300 text-2xl">→</div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-emerald-300 font-bold">After</div>
                <div className="text-3xl font-black bg-gradient-to-r from-emerald-300 to-amber-300 bg-clip-text text-transparent">{newPct}%</div>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4 text-sm font-extrabold text-amber-300">
              <Clock className="w-4 h-4 animate-pulse" />
              <span className="tabular-nums" data-testid="switcher-exit-countdown">Expires in {String(remaining).padStart(2, "0")}s</span>
            </div>

            <button
              type="button"
              onClick={accept}
              className="w-full bg-gradient-to-r from-emerald-400 to-amber-300 text-emerald-950 font-extrabold py-3.5 rounded-xl uppercase tracking-wider text-sm hover:from-emerald-300 hover:to-amber-200 transition-all shadow-[0_8px_30px_rgba(16,185,129,0.4)] flex items-center justify-center gap-2"
              data-testid="switcher-exit-accept"
            >
              <Gift className="w-4 h-4" /> Claim {newPct}% Off Now
            </button>
            <button
              type="button"
              onClick={() => setShow(false)}
              className="w-full text-white/40 hover:text-white/70 text-xs font-bold mt-3"
              data-testid="switcher-exit-decline"
            >
              No thanks, I'll pass on the savings
            </button>
          </>
        ) : (
          <div className="text-center py-2" data-testid="switcher-exit-accepted">
            <div className="inline-flex items-center gap-2 text-emerald-300 font-extrabold text-sm tracking-widest uppercase mb-2">
              <Gift className="w-4 h-4" /> {newPct}% locked in
            </div>
            <p className="text-white/80">Redirecting you to checkout…</p>
          </div>
        )}
      </div>
    </div>
  );
}
